import React, { FC, useRef, useCallback, useState, useEffect } from 'react';

import clsx from 'clsx';
import { useInView } from 'react-intersection-observer';
import { Player, IPlayerProps } from '@lottiefiles/react-lottie-player';
import { AnimationItem } from 'lottie-web';

import decorate from '../decorate/decorate';
import Box from '../box/box';
import Button from '../button/button';

export interface VisibleLottieProps extends Omit<IPlayerProps, 'src' | 'loop'> {
  /** Alt description of the animation */
  alt?: string;
  /** Allows the component to get additional styles. */
  className?: string;
  /** The lottie animation JSON data. */
  data?: string;
  /** Set the initial value of the inView boolean. */
  isInitialInView?: boolean;
  /** The path the lottie animation JSON file. */
  path?: string;
  /** Lottie Player className. */
  playerClassName?: string;
  /** The threshold for when the component is in the viewport. */
  threshold?: number;
  /** Should the Lottie Animation loop. */
  willLoop?: boolean;
  /** Include the canvas grid background image. */
  willShowDefaultBackground?: boolean;
  /** A method to execute when animation is paused. */
  onTogglePause?: (paused: boolean) => void;
}

const VisibleLottieComponent: FC<VisibleLottieProps> = ({
  autoplay,
  background,
  className,
  data,
  id = 'visibleLottie',
  isInitialInView = true,
  willLoop = false,
  path,
  playerClassName = 'visibleLottiePlayer',
  renderer = 'svg',
  rendererSettings,
  style,
  threshold = 0.5,
  willShowDefaultBackground = true,
  onTogglePause,
  ...rest
}) => {
  const [willShowControls, setWillShowControls] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [animationData, setAnimationData] = useState<string | object | undefined>(undefined);
  const playerRef = useRef<Player>(null);
  const lottieRef = useRef<AnimationItem>();
  const containerRef = useRef(null);
  const [inViewRef, isInView] = useInView({
    threshold,
    initialInView: isInitialInView,
  });
  const lottieRefs = useCallback(
    node => {
      lottieRef.current = node;
      if (isInView && !isPaused) {
        lottieRef.current?.play();
      }
    },
    [isInView, isPaused],
  );

  useEffect(() => {
    const animationData: string | object | undefined = data ? JSON.parse(data) : path;

    setAnimationData(animationData);
  }, [data, path]);

  useEffect(() => {
    if (onTogglePause) onTogglePause(isPaused);
    if (isPaused) return lottieRef?.current?.pause();
    if (isInView) lottieRef?.current?.play();
  }, [isPaused, onTogglePause, isInView]);

  const containerRefs = useCallback(
    node => {
      containerRef.current = node;
      inViewRef(node);
    },
    [inViewRef, containerRef],
  );

  const lottieRendererSettings = {
    ...rendererSettings,
    filterSize: {
      width: '200%',
      height: '200%',
      x: '-50%',
      y: '-50%',
    },
  };

  if (!animationData) return null;

  return (
    <Box
      ref={containerRefs}
      css={[
        willShowDefaultBackground && {
          background:
            'url(https://cdn-cashy-static-assets.lucidchart.com/lucidspark/marketing/pages/home/canvas-grid.svg) center center no-repeat',
        },
        willShowControls && {
          '& > button': {
            clip: 'auto',
            clipPath: 'none',
            height: 'auto',
            width: 'auto',
          },
        },
        {
          position: 'relative',
          '&:hover > button, & > button:focus-visible': {
            clip: 'auto',
            clipPath: 'none',
            height: 'auto',
            width: 'auto',
          },
        },
      ]}
      // eslint-disable-next-line react/jsx-sort-props
      className={clsx('visibleLottieContainer', className)}
      onClick={() => setWillShowControls(true)}
      onMouseLeave={() => setWillShowControls(false)}
    >
      <Player
        {...rest}
        ref={playerRef}
        keepLastFrame
        autoplay={autoplay}
        background={background}
        className={playerClassName}
        id={id}
        loop={willLoop}
        lottieRef={lottieRefs}
        renderer={renderer}
        rendererSettings={lottieRendererSettings}
        src={animationData}
        style={style}
      />
      <Button
        aria-label="Pause animation"
        aria-pressed={isPaused}
        css={{
          position: 'absolute',
          left: '50%',
          bottom: '0',
          clip: 'rect(0 0 0 0)',
          clipPath: 'inset(50%)',
          height: '1px',
          width: '1px',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          transform: 'translate(-50%, 0)',
        }}
        onClick={() => setIsPaused(!isPaused)}
        size="small"
        variant="primary"
      >
        {isPaused ? 'Play' : 'Pause'}
      </Button>
    </Box>
  );
};

// manually adding display name or it goes missing on build
VisibleLottieComponent.displayName = 'VisibleLottie';
const VisibleLottie = decorate<VisibleLottieProps>(VisibleLottieComponent);

export { VisibleLottie };
export default VisibleLottie;
